import React, { useMemo, useCallback, useState, useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { Icons } from "@texada/ui";
import {
  UserPermissions,
  useHasPermission
} from "components/utils/PermissionCheck";
import {
  BookingPermission,
  BookingType,
  BookingUrl
} from "pages/bookings/BookingType";
import { TSelectedMenu } from "./Sidebar";
import i18n from "i18n/i18n";

export interface BaseMenuItem {
  label: string;
  url: string;
  permission?: keyof Models.CompanyPermissions | keyof UserPermissions | "";
  feature?: keyof Models.CompanyFeatures;
}

export interface MenuItem extends Omit<BaseMenuItem, "url"> {
  section: string;
  children?: BaseMenuItem[];
}

export function getMenuItems(selectedMenu: TSelectedMenu): MenuItem[] {
  switch (selectedMenu) {
    case "texada-web":
      return [
        {
          label: i18n.t("dashboard"),
          permission: "dashboard",
          section: "/dashboard",
          children: [
            {
              label: i18n.t("console"),
              permission: "dashboard_sub",
              url: "/dashboard/console"
            },
            {
              label: i18n.t("calendar"),
              permission: "calendar",
              url: "/dashboard/calendar"
            }
          ]
        },
        {
          label: i18n.t("tickets"),
          permission: "tickets",
          section: "/tickets",
          children: [
            {
              label: i18n.t("pickup"),
              permission: "view_pickup",
              url: "/tickets/pickup/list"
            },
            {
              label: i18n.t("delivery"),
              permission: "view_delivery",
              url: "/tickets/delivery/list"
            },
            {
              label: i18n.t("task"),
              permission: "view_task",
              url: "/tickets/task"
            },
            {
              label: i18n.t("map"),
              url: "/tickets/map"
            },
            {
              label: i18n.t("scheduling"),
              permission: "view_ticket_schedule",
              url: "/tickets/scheduling"
            },
            {
              label: i18n.t("create_ticket"),
              permission: "create_tickets_sub",
              url: "/tickets/create"
            }
          ]
        },
        {
          label: i18n.t("Work_orders"),
          permission: "workorder",
          section: "/workorders",
          children: [
            {
              label: i18n.t("list"),
              url: "/workorders/list"
            },
            {
              label: i18n.t("map"),
              url: "/workorders/map"
            },
            {
              label: i18n.t("scheduling"),
              url: "/workorders/scheduling"
            },
            {
              label: i18n.t("create_work_order"),
              permission: "create_workorder_sub",
              url: "/workorders/create"
            },
            {
              label: i18n.t("create_work_order_estimate"),
              feature: "workorder_estimate",
              permission: "create_estimates",
              url: "/workorders/estimates/create"
            }
          ]
        },
        {
          label: i18n.t("bookings"),
          permission: "booking",
          section: "/bookings",
          children: [
            {
              label: i18n.t("Reservations"),
              permission: BookingPermission[BookingType.Reservations],
              url: BookingUrl[BookingType.Reservations]
            },
            {
              label: i18n.t("quotes"),
              permission: BookingPermission[BookingType.Quotes],
              url: BookingUrl[BookingType.Quotes]
            },
            {
              label: i18n.t("all_in_one"),
              permission: BookingPermission[BookingType.AllInOne],
              url: BookingUrl[BookingType.AllInOne]
            },
            {
              label: i18n.t("check_outs"),
              feature: "check_out",
              url: "/bookings/check-out/list"
            }
          ]
        },
        {
          label: i18n.t("Contracts"),
          permission: "contracts",
          section: "/contracts",
          children: [
            {
              label: i18n.t("list"),
              url: "/contracts/list"
            },
            {
              label: i18n.t("draft_list"),
              url: "/contracts/draft/list"
            },
            {
              label: i18n.t("calendar"),
              url: "/contracts/calendar"
            },
            {
              label: i18n.t("create_contract"),
              permission: "create_contracts_sub",
              url: "/contracts/create"
            }
          ]
        },
        {
          label: i18n.t("invoices"),
          permission: "view_invoices",
          section: "/invoices",
          children: [
            {
              label: i18n.t("list"),
              url: "/invoices/list"
            },
            {
              label: i18n.t("create_invoice"),
              permission: "create_invoices",
              url: "/invoices/create"
            }
          ]
        },
        {
          label: i18n.t("customers"),
          permission: "customer",
          section: "/customers",
          children: [
            {
              label: i18n.t("list"),
              url: "/customers/list"
            },
            {
              label: i18n.t("create_customer"),
              permission: "create_customer_sub",
              url: "/customers/create"
            }
          ]
        },
        {
          label: i18n.t("returns"),
          permission: "returns",
          section: "/returns",
          children: [
            {
              label: i18n.t("check-in"),
              url: "/returns/list"
            },
            {
              label: i18n.t("rental_returns"),
              permission: "create_rental_returns",
              url: "/returns/rentalreturn/1"
            }
          ]
        },
        {
          label: i18n.t("inventory"),
          permission: "inventory",
          section: "/inventory",
          children: [
            {
              label: i18n.t("assets"),
              url: "/inventory/asset/list"
            },
            {
              label: i18n.t("create_asset"),
              permission: "create_assets",
              url: "/inventory/asset/create"
            },
            {
              label: i18n.t("customer_owned"),
              url: "/inventory/customer-owned/list",
              feature: "customer_owned_equipment"
            },
            {
              label: i18n.t("create_customer_owned"),
              url: "/inventory/customer-owned/create",
              feature: "customer_owned_equipment"
            },
            {
              label: i18n.t("parts"),
              url: "/inventory/part/list"
            },
            {
              label: i18n.t("create_part"),
              permission: "create_parts",
              url: "/inventory/part/create"
            },
            {
              label: i18n.t("requested_parts"),
              url: "/inventory/part/requested"
            },
            {
              label: i18n.t("quick_receiving"),
              permission: "inventory_receiving",
              url: "/inventory/receiving"
            }
          ]
        },
        {
          label: i18n.t("operators"),
          permission: "operators",
          section: "/operators",
          children: [
            {
              label: i18n.t("drivers"),
              url: "/operators/drivers"
            },
            {
              label: i18n.t("mechanics"),
              url: "/operators/mechanics"
            }
          ]
        },
        {
          label: i18n.t("time_tracker"),
          permission: "time_tracker",
          section: "/timetracker"
        }
      ];

    case "gateway":
      return [
        {
          label: i18n.t("users"),
          section: "/gateway/users",
          children: [
            {
              label: i18n.t("user_list"),
              url: "/gateway/users/list"
            },
            {
              label: i18n.t("permissions"),
              url: "/gateway/users/permissions"
            },
            {
              label: i18n.t("product_access"),
              url: "/gateway/users/product-access"
            }
          ]
        },
        {
          label: i18n.t("inventory"),
          section: "/gateway/inventory",
          children: [
            {
              label: i18n.t("product_list"),
              url: "/gateway/inventory/list"
            },
            {
              label: i18n.t("class_or_group_settings"),
              url: "/gateway/inventory/class-group-settings"
            },
            {
              label: i18n.t("categories"),
              url: "/gateway/inventory/categories"
            },
            {
              label: i18n.t("attributes"),
              url: "/gateway/inventory/attributes"
            },
            {
              label: i18n.t("marketing_code"),
              url: "/gateway/inventory/marketing-code"
            }
          ]
        },
        {
          label: i18n.t("content"),
          section: "/gateway/content",
          children: [
            {
              label: i18n.t("homepage"),
              url: "/gateway/content/homepage"
            },
            {
              label: i18n.t("menu"),
              url: "/gateway/content/menu"
            },
            {
              label: i18n.t("pages"),
              url: "/gateway/content/pages"
            },
            {
              label: i18n.t("carousel"),
              url: "/gateway/content/carousel"
            },
            {
              label: i18n.t("modals"),
              url: "/gateway/content/modals"
            },
            {
              label: i18n.t("email_template"),
              url: "/gateway/content/email-template"
            }
          ]
        },
        {
          label: i18n.t("logs"),
          section: "/gateway/logs",
          children: [
            {
              label: i18n.t("web_requests"),
              url: "/gateway/logs/orders"
            },
            {
              label: i18n.t("service_requests"),
              url: "/gateway/logs/service-requests"
            },
            {
              label: i18n.t("emails"),
              url: "/gateway/logs/emails"
            }
          ]
        },
        {
          label: i18n.t("settings"),
          section: "/gateway/settings",
          children: [
            {
              label: i18n.t("store_config"),
              url: "/gateway/settings/store-config"
            },
            {
              label: i18n.t("divisions"),
              url: "/gateway/settings/divisions"
            },
            {
              label: i18n.t("email_server"),
              url: "/gateway/settings/email-server"
            },
            {
              label: i18n.t("hubspot"),
              url: "/gateway/settings/hubspot"
            }
          ]
        }
      ];
    case "settings":
      return [
        {
          label: i18n.t("companies"),
          section: "/setting/company"
        },
        {
          label: i18n.t("divisions"),
          section: "/setting/divisions"
        }
      ];

    default:
      return [];
  }
}

function isMenuItem(item: MenuItem | BaseMenuItem): item is MenuItem {
  return "section" in item;
}

export function getMenuItemUrl(item: MenuItem | BaseMenuItem) {
  if (isMenuItem(item)) {
    if ("children" in item) return item.children[0].url;
    return item.section;
  }
  return item.url;
}

export function useMenuItems(selectedMenu: TSelectedMenu) {
  const location = useLocation();
  const { hasPermission, hasFeature } = useHasPermission();
  const [selectedItem, setSelectedItem] = useState<MenuItem>();

  useEffect(() => {
    setSelectedItem(undefined);
  }, [location.pathname]);

  const menuItems = useMemo(() => {
    const menuItems = getMenuItems(selectedMenu);

    function deleteItems(items: (MenuItem | BaseMenuItem)[]) {
      for (const key in items) {
        const value = items[key];
        if (value === undefined) continue;

        if (!hasPermission(value.permission) || !hasFeature(value.feature)) {
          delete items[key];
        } else if ("children" in value) {
          value.children = deleteItems(value.children) as BaseMenuItem[];
        }
      }
      return items.filter(Boolean);
    }

    return deleteItems(menuItems).filter(item => {
      if ("children" in item) {
        return item.children.length > 0;
      }
      return true;
    });
  }, [hasPermission, selectedMenu]);

  const activeItems = useMemo(() => {
    const activeItems = new Set<MenuItem | BaseMenuItem>();

    function findActiveItem(items: (MenuItem | BaseMenuItem)[]) {
      for (const item of items) {
        const url = "url" in item ? item.url : item.section;

        const match = matchPath(location.pathname, {
          path: url,
          exact: false
        });

        if (match) {
          if ("children" in item) {
            findActiveItem(item.children);
          }
          activeItems.add(item);
          break;
        }
      }
    }
    findActiveItem(menuItems);

    return activeItems;
  }, [menuItems, location.pathname]);

  const isActive = useCallback(
    item => {
      if (selectedItem) {
        return item === selectedItem;
      }
      return activeItems.has(item);
    },
    [activeItems, selectedItem]
  );

  return { menuItems, isActive, setSelectedItem };
}

export const getIcon = item => {
  switch (item) {
    case "Tickets":
      return <Icons.Tickets />;
    case "Work Orders":
      return <Icons.Tools />;
    case "Bookings":
      return <Icons.EventAvailable />;
    case "Contracts":
      return <Icons.Contract />;
    case "Dashboard":
      return <Icons.Dashboard />;
    case "Invoices":
      return <Icons.Invoices />;
    case "Customers":
      return <Icons.Group />;
    case "Returns":
      return <Icons.Return />;
    case "Inventory":
      return <Icons.Inventory />;
    case "Operators":
      return <Icons.Operator />;
    case "Time Tracker":
      return <Icons.WatchLater />;
    case "Logs":
      return <Icons.Receipt />;
    case "Content":
      return <Icons.FileCopy />;
    case "Products":
      return <Icons.Inventory />;
    case "Users":
      return <Icons.Group />;
    case "Settings":
      return <Icons.Settings />;
    case "Companies":
      return <Icons.Store />;
    case "Divisions":
      return <Icons.AccountBalance />;
  }
};
